import React, {useContext, useEffect, useRef, useState} from 'react'
import {AthleteSwitchLeftBlue, AthleteSwitchLeftRed, useStyles, VideoCutSlider} from "./GeneralEditScoredSceneForm.css";
import {
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormHelperText,
    IconButton,
    ListSubheader,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import useGeneralEditScoredSceneForm from "./useGeneralEditScoredSceneForm";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {
    ArrowBackIos,
    ArrowDropDown,
    ArrowForwardIos,
    Brush,
    CheckCircleOutline,
    ChevronLeft,
    ChevronRight,
    ContentCut,
    Delete,
    ErrorOutline,
    Flag,
    Fullscreen,
    FullscreenExit,
    Pause,
    Pending,
    PictureInPicture,
    PlayArrow,
    SlowMotionVideo,
    SportsMartialArts,
    SwapHoriz
} from "@mui/icons-material";
import moment from "moment";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import {motion} from "framer-motion"
import DrawingSection from "../drawing/drawingDefinition/DrawingSection";
import ScoreEnrichedVideoProgressSlider from "./sliders/ScoreEnrichedVideoProgressSlider";
import {useIntl} from "react-intl";
import generalConfig from "../../../../commons/associationStructure/generalConfig.json";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import hash from "object-hash";
import {Prompt} from 'react-router'
import BasicHint from "../../../../commons/utils/hint/BasicHint";
import SuggestionTextFieldMulti from "../../../../commons/elements/autocomplete/SuggestionTextFieldMulti";
import useLoadScoredScenesAutocompleteOptions from "./useLoadScoredScenesAutocompleteOptions";
import SuggestionTextField from "../../../../commons/elements/autocomplete/SuggestionTextField";
import {TabContext, TabList, TabPanel} from "@mui/lab";
import {alpha} from "@mui/material/styles";
import AssociationLogo from "../../../../commons/utils/logo/AssociationLogo";
import useListScoredScenes from "../overview/useListScoredScenes";


const GeneralEditScoredSceneForm = ({
                                        selectedVideo,
                                        selectedScoredScene,
                                        editedScoredSceneRef,
                                        setSaveButtonEnabled
                                    }) => {
    const classes = useStyles()

    const {
        // Form states
        difficulty,
        mainFocus,
        trailingPeriod,
        athleteLeftPosition,
        videoProgress,
        videoRange,
        scoreDefinitions,

        // Screen control
        screenReady,

        // Video utils
        originalVideoDuration,
        videoPlaying,

        // Playback rate
        handleTogglePlaybackRate,
        isSlowPlaybackRate,

        // Dimensions of the video iFrame (used for overlaying the drawing area)
        canvasWidth,
        canvasHeight,
        canvasLeft,
        canvasTop,

        // Helper functions for editing the score definitions
        addScoreDefinition,
        addConcurrentScoreDefinition,
        removeScoreDefinition,
        pictureForward,
        pictureBack,
        jumpToScoreDefinition,

        // Fast forward / fast rewind
        fastForward,
        fastRewind,

        // Handlers for updating the states and scored scene reference according to the user inputs
        handleChangeAthlete,
        handleChangeTechnique,
        handleChangeTechniqueFreeText,
        handleChangeDifficulty,
        handleChangeMainFocus,
        handleChangeTrailingPeriod,
        switchAthleteLeftPosition,

        // Video controls
        updateVideoRange,
        updateVideoProgress,
        setCurrentVideoTimeInScoreDefinition,
        playVideo,
        pauseVideo,
        fullScreenMode,
        setFullScreenMode,

        // Drawing
        activeDrawingScoreDefinition,
        drawingMode,
        previewInProgress,
        videoProgressInHighlightingRange,
        showPreview,
        handleEnterDrawingMode,
        handleLeaveDrawingMode,
        handleSaveDrawing,
        handleDeleteDrawing,

        // Blurring
        blurringDefinition,
        blurDrawingMode,
        showBlurPreview,
        handleEnterBlurringMode,
        handleLeaveBlurringMode,
        handleSaveBlurring,
        handleDeleteBlurring,

        // Video dimensions
        videoHeight,
        videoWidth,

        // Tabs
        activeScoreDefinitionIndex,
        handleChangeScoreDefinitionTab,

        // Video cut slider width for visualizing already created scored scene ranges
        videoCutSliderWidth
    } = useGeneralEditScoredSceneForm(selectedScoredScene, selectedVideo, editedScoredSceneRef)

    // Fetch all scored scenes of the related video, so that we can show all already created time range on the video cut slider
    const {scoredScenes} = useListScoredScenes(selectedVideo.key, null)

    // All scored scenes of the video, except the currently edited one - if in "new" mode, it remains null
    const [allOtherScoredScenesOfVideo, setAllOtherScoredScenesOfVideo] = useState(null)

    // Handling for hinting the user to save changes in case he tries to leave the form after making changes
    const initialHash = useRef(null)
    const [hasFormChanged, setHasFormChanged] = useState(false)

    // Internationalization for simple strings
    const intl = useIntl();

    // Get permission-based limited association structure / Checking if the edit/saving operations are allowed
    const {allowedAssociationStructure, isOperationOnObjectAllowed} = useContext(FirebaseAuthContext);

    // Information about the discipline
    const disciplineStructure = allowedAssociationStructure[selectedVideo.basics.association].disciplines.find((d) => d.name === selectedVideo.basics.discipline)

    // Available options for selecting the main focus
    const {fetchAutocompleteOptions} = useLoadScoredScenesAutocompleteOptions()
    const [autocompleteOptions, setAutocompleteOptions] = useState({})

    // State for showing/hiding the video overlay controls on mouse enter/leave
    const [showVideoControls, setShowVideoControls] = useState(true)

    useEffect(() => {
        setSaveButtonEnabled(isOperationOnObjectAllowed(selectedScoredScene) && areAllScoreDefinitionsValid(scoreDefinitions) && isVideoSceneLengthValid() && difficulty && mainFocus.length > 0 && isBlurringWithinFence() && !drawingMode && !blurDrawingMode)

        // Refresh the current hash and set the initial hash at the very beginning
        const currentHash = hash(editedScoredSceneRef.current, {unorderedArrays: true})
        if (!initialHash.current) {
            initialHash.current = currentHash
        }
        setHasFormChanged(initialHash.current !== currentHash)

    }, [scoreDefinitions, videoRange, difficulty, mainFocus, trailingPeriod, blurringDefinition, drawingMode, blurDrawingMode, athleteLeftPosition])

    // Fetch the autocomplete options based on the video's given association and discipline (for the main focus)
    useEffect(() => {
        fetchAutocompleteOptions(selectedVideo.basics.association, selectedVideo.basics.discipline)
            .then((options) => {
                setAutocompleteOptions(options)
            })
    }, [])

    // Set the state holding all scored scenes of the related video, except the currently edited one (remains null if in "new" mode)
    useEffect(() => {
        if (scoredScenes?.length > 0 && !allOtherScoredScenesOfVideo) {
            setAllOtherScoredScenesOfVideo(scoredScenes.filter((s) => s.key !== selectedScoredScene?.key))
        }
    }, [scoredScenes, selectedScoredScene])

    /*
    Plausibility checks
     */
    const areAllScoreDefinitionsValid = (scoreDefinitions) => {
        if (scoreDefinitions.length === 0) {
            return false
        }

        let isValid = true

        scoreDefinitions.forEach((s) => {
            // Hint: if the score is within the fence, it is also automatically within the range (i.e. no additional check necessary)
            if (!isScoreWithinFence(s) || !isEnoughTimeForDecision(s)) {
                isValid = false
            }
            if (!s.athlete || !s.score || !s.technique || (disciplineStructure.scores.find((sd) => sd.technique === s.technique)?.freeText && !s.techniqueFreeText)) {
                isValid = false
            }
        })
        return isValid
    }

    const isScoreWithinRange = (s) => {
        return (s.time >= videoRange[0] && s.time <= videoRange[1])
    }

    // The score must be neither close to the start nor the end of the video (without considering the trailing period)
    const isScoreWithinFence = (scoreDefinition) => {
        return ((scoreDefinition.time - videoRange[0]) > 1 && (videoRange[1] - scoreDefinition.time) > 0.5);
    }

    const isEnoughTimeForDecision = (scoreDefinition) => {
        // As trailing period will be fixed to 2 --> this will never be effective (in combination with the "isScoreWithinFence" restriction)
        return (videoRange[1] + trailingPeriod - scoreDefinition.time) > 2;
    }

    const getScoreTimeWarningText = (scoreDefinition) => {
        if (!isScoreWithinRange(scoreDefinition)) {
            return intl.formatMessage({
                id: 'app.scoredScene.edit.scores.warnings.outOfRange',
                defaultMessage: 'Time out of range!'
            })
        }
        if (!isScoreWithinFence(scoreDefinition)) {
            return intl.formatMessage({
                id: 'app.scoredScene.edit.scores.warnings.closeToEdge',
                defaultMessage: 'Too close to edge!'
            })
        }
        if (!isEnoughTimeForDecision(scoreDefinition)) {
            return intl.formatMessage({
                id: 'app.scoredScene.edit.scores.warnings.littleReaction',
                defaultMessage: 'Too little time left for reaction!'
            })
        }
    }

    const getBlurringWarningText = () => {
        if (!isBlurringWithinFence()) {
            return intl.formatMessage({
                id: 'app.scoredScene.edit.blur.warnings.outOfRange',
                defaultMessage: 'Blurring out of video range!'
            })
        }
    }

    const isBlurringWithinFence = () => {
        if (!blurringDefinition) {
            return true
        }
        return blurringDefinition.visibilityStart >= videoRange[0] && blurringDefinition.visibilityEnd <= videoRange[1]
    }

    const isVideoSceneLengthValid = () => {
        const len = videoRange[1] - videoRange[0]
        return len >= disciplineStructure.minDuration && len <= disciplineStructure.maxDuration
    }

    const getDurationInfoWarningText = () => {
        if (!isVideoSceneLengthValid()) {
            return <div className={classes.warningPanel}>
                <ErrorOutline className={classes.infoIcon}/>
                {intl.formatMessage({
                    id: 'app.scoredScene.edit.videoRangeSlider.duration.warning',
                    defaultMessage: 'Use the controls to trim the video: {minDuration}s - {maxDuration}s.'
                }, {minDuration: disciplineStructure.minDuration, maxDuration: disciplineStructure.maxDuration})}
            </div>
        }

        return <div className={classes.infoPanel}>
            <CheckCircleOutline className={classes.infoIcon}/>
            {intl.formatMessage({
                id: 'app.scoredScene.edit.videoRangeSlider.duration.info',
                defaultMessage: 'Valid video duration: {duration}'
            }, {duration: moment((videoRange[1] - videoRange[0]) * 1000).format("mm:ss:SS")})}
        </div>
    }

    const getCurrentVideoCutSliderThumbIndication = () => {
        return <div style={{marginTop: -48}}>
            <ArrowDropDown/>
        </div>
    }

    // END Plausibility checks

    const getRowClass = (scoreDefinition, index) => {
        if (!isScoreWithinRange(scoreDefinition)) {
            return classes.scoreOutOfRange
        }
        if (!isScoreWithinFence(scoreDefinition)) {
            return classes.scoreOutOfFence
        }
        if (!isEnoughTimeForDecision(scoreDefinition)) {
            return classes.scoreOutOfFence
        }
        return null
    }

    const showIfTrue = (text, condition) => {
        if (condition) {
            return text
        }
        return ''
    }

    const getScoringGroupsBasedOnDiscipline = () => {
        const list = []
        disciplineStructure.scores.forEach((technique) => {
            if (!list.includes(technique.score)) {
                list.push(technique.score)
            }
        })
        return list
    }

    const getTechniqueMenuItems = () => {
        const techniques = disciplineStructure.scores
        const scoreGroups = getScoringGroupsBasedOnDiscipline()

        const list = []

        scoreGroups.forEach((scoreGroup) => {
            list.push(<ListSubheader key={scoreGroup}
                                     className={classes.menuItemHeader}><b>Decision: {scoreGroup}</b></ListSubheader>)
            list.push(techniques.filter((techniqueEntry) => techniqueEntry.score === scoreGroup).map((techniqueEntryInScoreGroup) => {
                return <MenuItem key={techniqueEntryInScoreGroup.technique}
                                 value={techniqueEntryInScoreGroup.technique}>
                    {intl.formatMessage({
                        id: `app.ids.technique.${techniqueEntryInScoreGroup.technique}`,
                        defaultMessage: techniqueEntryInScoreGroup.technique
                    })}
                </MenuItem>
            }))
        })
        return list
    }

    const areAdditionalScoreDefinitionsAllowed = () => {
        return disciplineStructure.mode === 'running_time' || scoreDefinitions.length === 0
    }

    const isConcurrentScoreDefinitionAllowed = () => {
        return disciplineStructure.mode === 'point_stop' && scoreDefinitions.length === 1
    }

    const getNewDecisionSection = () => {
        if (areAdditionalScoreDefinitionsAllowed()) {
            return <Button variant="outlined"
                           style={{minWidth: 250}}
                           onClick={addScoreDefinition}
                           disabled={!isOperationOnObjectAllowed(selectedScoredScene)}>
                                <span>
                                    {intl.formatMessage({
                                        id: 'app.scoredScene.edit.scores.headerBar.actions.newDecision',
                                        defaultMessage: 'New Decision at <code>{time}</code>'
                                    }, {
                                        time: moment(videoProgress * 1000).format("mm:ss:SS"),
                                        code: (component) => <b>{component}</b>
                                    })}
                                </span>
            </Button>
        }

        if (isConcurrentScoreDefinitionAllowed()) {
            return <Button variant="outlined"
                           onClick={addConcurrentScoreDefinition}>
                                <span>
                                    {intl.formatMessage({
                                        id: 'app.scoredScene.edit.scores.headerBar.actions.newConcurrentDecision',
                                        defaultMessage: 'Concurrent Decision at <code>{time}</code>'
                                    }, {
                                        time: moment(scoreDefinitions[0].time * 1000).format("mm:ss:SS"),
                                        code: (component) => <b>{component}</b>
                                    })}
                                </span>
            </Button>
        }

        return <b>
            {intl.formatMessage({
                id: 'app.scoredScene.edit.scores.headerBar.warnings.noFurtherDecisionsAllowed',
                defaultMessage: 'No additional score definitions allowed for discipline <code>{discipline}</code>'
            }, {
                discipline: intl.formatMessage({
                    id: `app.ids.discipline.${selectedVideo.basics.discipline}`,
                    defaultMessage: selectedVideo.basics.discipline
                }),
                code: (component) => <em>{component}</em>
            })}
        </b>
    }

    // Score marks for highlighting and blurring time range
    const getDrawingVisibilityRangeScoreMark = (score, optionalConcurrentScore) => {
        return <div className={classes.row}>
            {[score, optionalConcurrentScore].map((sd, index) => {
                if (sd) {
                    return <div key={index} className={sd.athlete === 'blue'
                        ? classes.highlightingSliderRoundScoreBlue
                        : classes.highlightingSliderRoundScoreRed}>
                        {sd.score}
                    </div>
                }
                return ''
            })}
        </div>
    }

    const getClassInBlurMode = (classWithoutBlur, condition) => {
        if (condition) {
            return `${classWithoutBlur} ${classes.blur}`
        } else {
            return classWithoutBlur
        }
    }

    const getScoreDefinitionTabLabel = (scoreDefinition) => {
        const techniqueFreeTextRequired = !!disciplineStructure.scores.find((s) => s.technique === scoreDefinition.technique)?.freeText

        // Show a pending icon in case the score definition is not yet complete
        if (!(scoreDefinition.score
            && scoreDefinition.athlete
            && scoreDefinition.frameAlreadySeeked
            && ((techniqueFreeTextRequired && scoreDefinition.techniqueFreeText)
                || !techniqueFreeTextRequired))) {
            return <Pending/>
        }

        // Show a nice blue/red score circle including the score number
        return <div
            className={scoreDefinition.athlete === 'blue' ? classes.scoreTabLabelBlue : classes.scoreTabLabelRed}>
            {scoreDefinition.score}
        </div>
    }

    const getRangeMarksOfOtherScoredScenes = () => {
        if (allOtherScoredScenesOfVideo) {
            return [

                // Indicate the time ranges of already created scored scenes (middle)
                ...allOtherScoredScenesOfVideo?.map((s) => {
                    const vDuration = s.video.vimeo.duration
                    const sStart = s.basics.sceneStart
                    const sEnd = s.basics.sceneEnd
                    const sDuration = sEnd - sStart
                    const durationMid = sStart + (sEnd - sStart) / 2

                    const relativeDurationWidth = 100 / vDuration * sDuration / 100
                    const absoluteDurationWidth = videoCutSliderWidth * relativeDurationWidth

                    return {
                        value: durationMid,
                        label: <div style={{
                            backgroundColor: "#CE93D8",
                            marginTop: -21,
                            width: absoluteDurationWidth,
                            height: 10,
                            borderLeft: "3px solid",
                            borderRight: "3px solid",
                            borderRadius: 15,
                            opacity: 0.7
                        }}/>
                    }
                })
            ]
        }
        return []
    }

    const getFactor = () => {

        if (fullScreenMode) {
            return 0.9
        }

        if (previewInProgress) {
            return 0.9
        }

        if (blurDrawingMode || drawingMode) {
            return 0.9
        }

        return 0.75
    }

    return (
        <div>
            {/* Security prompt if the user tries to (maybe unintendendly) leave the page.
                This needs to be effective if there are changes made to the current object. */}
            <Prompt
                when={hasFormChanged}
                message={intl.formatMessage({
                    id: 'app.utils.unsavedChanges.hint',
                    defaultMessage: 'Your changes are not saved, are you sure you want to leave?'
                })}
            />

            {/* Loading/Preparation indication */}
            {!screenReady && <div className={classes.progress}>
                <CircularProgress/>
                <Typography>
                    {intl.formatMessage({
                        id: 'app.scoredScene.edit.preparation.loading.info',
                        defaultMessage: 'We prepare the video for you!'
                    })}
                </Typography>
                <Typography>
                    {intl.formatMessage({
                        id: 'app.scoredScene.edit.preparation.loading.timeIndication',
                        defaultMessage: 'This might take a few seconds...'
                    })}
                </Typography>
            </div>}

            {/* IMPORTANT: we only "hide" this div while the screen is not ready, because the Vimeo SDK needs the player iFrame! */}
            <div hidden={!screenReady} className={classes.root}>

                {/* Video panel + Video metadata */}
                <div className={classes.topRow}>

                    {/* Video section including various overlays */}
                    <div>

                        {/* Video frame - can be controlled by the Vimeo SDK + Canvas for drawing on the video frame + overlay video controls.
                            The width of the video is calculated based on the viewport height, so that everything fits without the necessity of vertical scrolling. */}
                        <div style={{
                            position: "relative",
                            width: videoWidth !== 0 && videoHeight !== 0
                                ? (videoWidth / videoHeight) * (window.innerHeight - 70) * (getFactor())
                                : 0
                        }}
                             onMouseOver={(event) => setShowVideoControls(true)}
                             onMouseLeave={(event) => setShowVideoControls(false)}>

                            {/* The video iFrame is kept in a container, which keeps the ratio of the actual video.
                                This is important, because the iFrame might have a huge padding area. */}

                            <div id="iFrameContainer"
                                 style={{position: "relative", paddingTop: `${videoHeight / videoWidth * 100}%`}}>
                                <iframe id="videoPlayer" title="videoPlayer" width="100%" height="100%"
                                        src={selectedVideo.vimeo.playerEmbedUrl + '&background=1'}
                                        frameBorder="0" allowFullScreen="" allow="autoplay"
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0
                                        }}/>
                            </div>

                            {/* Full screen mode */}
                            {!previewInProgress && !blurDrawingMode && !drawingMode && <div style={{
                                position: "absolute",
                                top: 10,
                                right: 10,
                                zIndex: 1
                            }}>

                                {!fullScreenMode
                                    ? <IconButton className={classes.fullScreenButton}
                                                  onClick={() => setFullScreenMode(true)}>
                                        <Fullscreen className={classes.fullScreenIcon}/>
                                    </IconButton>
                                    :
                                    <div className={classes.fullScreenContainer}>
                                        <div className={classes.fullScreenDescription}>
                                            {intl.formatMessage({
                                                id: 'app.scoredScene.messages.fullScreen.exit.hint',
                                                defaultMessage: 'Leave full-screen mode to see further functionalities'
                                            })}
                                        </div>
                                        <IconButton className={classes.fullScreenButton}
                                                    onClick={() => setFullScreenMode(false)}>
                                            <FullscreenExit className={classes.fullScreenIcon}/>
                                        </IconButton>
                                    </div>}

                            </div>}

                            {/* Highlighting Section (Drawing + Preview) */}
                            <DrawingSection

                                // Init the value always with the current value stored in the reference (the onSave method will updated it)
                                initDrawing={activeDrawingScoreDefinition && activeDrawingScoreDefinition.highlighting}

                                // Orientation for the blurring area
                                canvasTop={canvasTop}
                                canvasLeft={canvasLeft}
                                canvasHeight={canvasHeight}
                                canvasWidth={canvasWidth}

                                // Start end ranges used for determining the slider range for the blurring time
                                visibilityRangeMin={activeDrawingScoreDefinition && activeDrawingScoreDefinition.time - 0.5}
                                visibilityRangeMax={activeDrawingScoreDefinition && activeDrawingScoreDefinition.time + 0.5}

                                // Set the default values tighter around the mid of the video
                                visibilityRangeStart={activeDrawingScoreDefinition && activeDrawingScoreDefinition.time - 0.25}
                                visibilityRangeEnd={activeDrawingScoreDefinition && activeDrawingScoreDefinition.time + 0.25}

                                // Toggles for showing / hiding certain elements (i.e. sliders and toolbar)
                                drawingMode={drawingMode}
                                previewMode={previewInProgress || videoProgressInHighlightingRange}

                                // Callback handlers
                                onSave={handleSaveDrawing}
                                onDeleteAll={handleDeleteDrawing}
                                onLeaveDrawingMode={handleLeaveDrawingMode}
                                onVisibilityRangeChange={updateVideoProgress}

                                // Marks (single mark to indicate where the score is defined)
                                timeRangeMarks={[{
                                    value: activeDrawingScoreDefinition?.time,
                                    label: getDrawingVisibilityRangeScoreMark(activeDrawingScoreDefinition, scoreDefinitions.find((sd) => sd.concurrent))
                                }]}

                                // Set blurring mode
                                mode="highlight"
                            />

                            {/* Blurring Section (Drawing + Preview) */}
                            <DrawingSection

                                // Init the value always with the current value stored in the reference (the onSave method will updated it)
                                initDrawing={blurringDefinition}

                                // Orientation for the blurring area
                                canvasTop={canvasTop}
                                canvasLeft={canvasLeft}
                                canvasHeight={canvasHeight}
                                canvasWidth={canvasWidth}

                                // Start end ranges used for determining the slider range for the blurring time
                                visibilityRangeMin={scoreDefinitions[0]?.time || videoRange[0]}
                                visibilityRangeMax={videoRange[1]}

                                // Set the same values as for the visibilityRangeStart/End
                                visibilityRangeStart={scoreDefinitions[0]?.time || videoRange[0]}
                                visibilityRangeEnd={videoRange[1]}

                                // Toggles for showing / hiding certain elements (i.e. sliders and toolbar)
                                drawingMode={blurDrawingMode}
                                previewMode={showBlurPreview && !previewInProgress && !drawingMode}

                                // Callback handlers
                                onSave={handleSaveBlurring}
                                onDeleteAll={handleDeleteBlurring}
                                onLeaveDrawingMode={handleLeaveBlurringMode}
                                onVisibilityRangeChange={updateVideoProgress}

                                // Marks (start = score | video end)
                                timeRangeMarks={[
                                    {
                                        value: scoreDefinitions[0]?.time,
                                        label: getDrawingVisibilityRangeScoreMark(scoreDefinitions[0], scoreDefinitions.find((sd) => sd.concurrent))
                                    },
                                    {
                                        value: videoRange[1],
                                        label: <Flag/>
                                    }
                                ]}

                                // Set blurring mode
                                mode="blur"
                            />

                            {/* Overlay "video cut" and "video progress" slider (absolute positioning as video overlay) */}
                            {/* We wait until the screen is ready (i.e. the video start/end is exactly determined, before we start rendering this) */}
                            {screenReady &&
                            <div hidden={(videoPlaying && !showVideoControls) || previewInProgress} style={{
                                position: "absolute",
                                bottom: 2,
                                left: 2,
                                width: canvasWidth - 4,
                                backgroundColor: alpha('#000000', 0.8),
                                marginRight: 100,
                            }}>

                                {/* Video cutting slider (2-dimensional) */}
                                {!videoPlaying && <fieldset disabled={!isOperationOnObjectAllowed(selectedScoredScene)}
                                                            style={{borderWidth: 0, margin: 0, padding: 0}}>
                                    {!drawingMode && !blurDrawingMode && <div className={classes.videoCutSlider}>
                                        <div className={classes.videoCutButtonContainer}>
                                            <IconButton size="large">
                                                <ContentCut
                                                    style={{width: 35, height: 35, marginBottom: 15, color: "white"}}/>
                                            </IconButton>
                                        </div>
                                        <VideoCutSlider id="videoCutSlider"
                                                        onChange={updateVideoRange}
                                                        value={videoRange}
                                                        valueLabelDisplay="auto"
                                                        valueLabelFormat={(value, index) => moment(value * 1000).format("mm:ss:SS")}
                                                        min={0}
                                                        max={originalVideoDuration}
                                                        step={0.01}
                                                        disabled={previewInProgress || videoPlaying || drawingMode}
                                                        outofrange={isVideoSceneLengthValid() ? 0 : 1} // Convert to number, as passing boolean variables is not possible here
                                                        marks={[
                                                            {
                                                                value: videoRange[0] + (videoRange[1] - videoRange[0]) / 2,
                                                                label: getDurationInfoWarningText()
                                                            },
                                                            {
                                                                value: videoProgress,
                                                                label: getCurrentVideoCutSliderThumbIndication()
                                                            },

                                                            // Indicate the time ranges of already created scored scenes
                                                            ...getRangeMarksOfOtherScoredScenes()
                                                        ]}
                                        />
                                    </div>}
                                </fieldset>}

                                {/* Video progress slider (1-dimensional) */}
                                {!drawingMode && !blurDrawingMode && <div className={classes.videoProgressSlider}>
                                    <div className={classes.playPauseButtonContainer}>
                                        <div className={classes.defaultPlaybackRateContainer}>
                                            <Checkbox value={isSlowPlaybackRate}
                                                      checked={isSlowPlaybackRate}
                                                      icon={<SlowMotionVideo
                                                          style={{width: 35, height: 35, color: "white"}}/>}
                                                      checkedIcon={<SlowMotionVideo style={{width: 35, height: 35}}/>}
                                                      onChange={handleTogglePlaybackRate}
                                                      style={{minWidth: 50}}/>
                                            <div style={{marginTop: -10}}>
                                                {isSlowPlaybackRate
                                                    ? intl.formatMessage({
                                                        id: 'app.scoredScene.edit.videoSlider.defaultPlaybackMode.slow.label',
                                                        defaultMessage: 'Slow'
                                                    })
                                                    : intl.formatMessage({
                                                        id: 'app.scoredScene.edit.videoSlider.defaultPlaybackMode.normal.label',
                                                        defaultMessage: 'Normal'
                                                    })}
                                            </div>
                                        </div>
                                        <div>
                                            {
                                                !videoPlaying
                                                    ? <IconButton
                                                        onClick={playVideo}
                                                        disabled={previewInProgress || drawingMode}
                                                        size="large">
                                                        <PlayArrow className={classes.playPauseIcon}/>
                                                    </IconButton>
                                                    : <IconButton onClick={pauseVideo} size="large">
                                                        <Pause className={classes.playPauseIcon}/>
                                                    </IconButton>
                                            }
                                        </div>
                                    </div>
                                    <ScoreEnrichedVideoProgressSlider
                                        onChange={(event, data) => updateVideoProgress(data)}
                                        value={videoProgress}
                                        disabled={previewInProgress || drawingMode}
                                        sceneStart={videoRange[0]}
                                        sceneEnd={videoRange[1]}
                                        blurringStart={blurringDefinition?.visibilityStart}
                                        blurringEnd={blurringDefinition?.visibilityEnd}
                                        trailingPeriod={trailingPeriod}
                                        scoreDefinitions={scoreDefinitions}
                                    />
                                </div>}
                            </div>}
                        </div>
                    </div>

                    {/* Metadata fields for the scored scene */}
                    {!previewInProgress && !blurDrawingMode && !drawingMode && !fullScreenMode &&
                    <fieldset disabled={!isOperationOnObjectAllowed(selectedScoredScene)}
                              style={{borderWidth: 0, margin: 0, padding: 0, width: "100%"}}>
                        <div className={classes.formColumn}>

                            {/* Association and discipline - Header */}
                            <div className={classes.headerContainer}>
                                <AssociationLogo association={selectedVideo.basics.association} height={50}
                                                 width={50}/>
                                <div>
                                    <div className={classes.headerText}>
                                        {intl.formatMessage({
                                            id: `app.ids.discipline.${selectedVideo.basics.discipline}`
                                        })}
                                    </div>
                                    <div className={classes.subtitleText}>
                                        {`${selectedVideo.basics.tournament} (${selectedVideo.basics.year})`}
                                    </div>
                                </div>
                                <div>
                                    <div className={classes.infoText}>
                                        {intl.formatMessage({
                                            id: `app.ids.ageGroup.${selectedVideo.basics.ageGroup}`
                                        }) + ' | ' + intl.formatMessage({
                                            id: `app.ids.gender.${selectedVideo.basics.gender}`
                                        })}
                                    </div>
                                    <div className={classes.infoText}>
                                        {selectedVideo.basics.surnameAthlete1} vs. {selectedVideo.basics.surnameAthlete2}
                                    </div>
                                </div>
                            </div>

                            {/* Positions of blue and red athlete at the beginning */}
                            <div className={classes.athletePositionBoxContainer}>
                                <motion.div
                                    animate={athleteLeftPosition === 'red' ? {scale: [1, 1.5, 1.5, 1, 1]} : {scale: [1, 1.5, 1.5, 1.01, 1]}}>
                                    <div
                                        className={athleteLeftPosition === 'red' ? classes.redBox : classes.blueBox}>
                                        <SportsMartialArts style={{height: 40, width: 40}}/>
                                    </div>
                                </motion.div>

                                <IconButton onClick={switchAthleteLeftPosition} size="large">
                                    <SwapHoriz style={{height: 40, width: 40}}/>
                                </IconButton>

                                <motion.div
                                    animate={athleteLeftPosition === 'red' ? {scale: [1, 1.5, 1.5, 1, 1]} : {scale: [1, 1.5, 1.5, 1.01, 1]}}>
                                    <div
                                        className={athleteLeftPosition === 'red' ? classes.blueBox : classes.redBox}>
                                        <SportsMartialArts
                                            style={{height: 40, width: 40, transform: "scaleX(-1)"}}/>
                                    </div>
                                </motion.div>
                            </div>

                            {/* Difficulty */}
                            <FormControl className={classes.difficultyContainer}>
                                <InputLabel className={classes.dropdownLabel} variant="outlined" required
                                            id="reg-video-difficulty-label">
                                    {intl.formatMessage({
                                        id: 'app.scoredScene.edit.form.difficulty.label',
                                        defaultMessage: 'Scoring Difficulty'
                                    })}
                                </InputLabel>
                                <Select variant="outlined" id="reg-video-difficulty-input"
                                        labelId="reg-video-difficulty-label"
                                        aria-describedby="reg-video-difficulty-help"
                                        value={difficulty} onChange={handleChangeDifficulty}>
                                    {generalConfig.difficulty.map((difficulty) =>
                                        <MenuItem key={difficulty} value={difficulty}>
                                            {intl.formatMessage({id: `app.ids.difficulty.${difficulty}`})}
                                        </MenuItem>
                                    )}
                                </Select>
                                <FormHelperText variant="outlined"
                                                id="reg-video-difficulty-help"
                                                error={!difficulty}>
                                    {showIfTrue(intl.formatMessage({
                                        id: 'app.scoredScene.edit.form.difficulty.help',
                                        defaultMessage: 'Enter difficulty'
                                    }), !difficulty)}</FormHelperText>
                            </FormControl>

                            {/* Main focus */}
                            <FormControl className={classes.mainFocusContainer}>
                                <SuggestionTextFieldMulti
                                    id='reg-video-mainFocus-input'
                                    limitTags={3}
                                    label={intl.formatMessage({
                                        id: 'app.scoredScene.edit.form.mainFocus.label',
                                        defaultMessage: 'Main Focus'
                                    })}
                                    placeholder={intl.formatMessage({
                                        id: 'app.scoredScene.edit.form.mainFocus.placeholder',
                                        defaultMessage: 'Add Main Focus'
                                    })}
                                    existingOptions={autocompleteOptions?.mainFocus || []}
                                    value={mainFocus}
                                    onChange={handleChangeMainFocus}
                                />
                                <FormHelperText variant="outlined" error={mainFocus.length === 0}
                                                id="reg-video-mainFocus-help">
                                    {showIfTrue(intl.formatMessage({
                                        id: 'app.scoredScene.edit.form.mainFocus.help',
                                        defaultMessage: 'Enter main focus'
                                    }), mainFocus.length === 0)}
                                </FormHelperText>
                            </FormControl>

                            {/* BLURRING: Set blurring / Reset blurring - only make blurring possible in case of point_stop disciplines */}
                            {disciplineStructure.mode === 'point_stop' && <div className={classes.blurringContainer}>
                                <div className={classes.blurringGroup}>
                                    <Button style={{width: "100%"}}
                                            disabled={blurDrawingMode || drawingMode || previewInProgress || videoPlaying || scoreDefinitions?.length === 0 || !scoreDefinitions.every((sd) => isScoreWithinRange(sd))}
                                            variant="outlined"
                                            onClick={handleEnterBlurringMode}>
                                        {
                                            !blurringDefinition
                                                ?
                                                <div>
                                                    {intl.formatMessage({
                                                        id: 'app.scoredScene.edit.blur.actions.define',
                                                        defaultMessage: 'Define Blurring Area'
                                                    })}
                                                </div>
                                                :
                                                <div>
                                                    <div><b>
                                                        {intl.formatMessage({
                                                            id: 'app.scoredScene.edit.blur.actions.update',
                                                            defaultMessage: 'Update Blurred Area'
                                                        })}
                                                    </b></div>
                                                    <div>
                                                        {intl.formatMessage({
                                                            id: 'app.scoredScene.edit.blur.actions.update.timeRangeInfo',
                                                            defaultMessage: 'Curr. Blurring: {startTime} - {endTime}'
                                                        }, {
                                                            startTime: moment(blurringDefinition.visibilityStart * 1000).format("mm:ss:SS"),
                                                            endTime: moment(blurringDefinition.visibilityEnd * 1000).format("mm:ss:SS")
                                                        })}
                                                    </div>
                                                </div>
                                        }
                                    </Button>
                                </div>
                                <div className={classes.blurringError}>
                                    {getBlurringWarningText()}
                                </div>
                            </div>}
                        </div>
                    </fieldset>}
                </div>

                {/* Score definition tabs (identified by the index, which corresponds to the scores on the timeline) */}
                {!previewInProgress && !blurDrawingMode && !drawingMode &&
                <TabContext value={activeScoreDefinitionIndex.toString()}>

                    {/* Decision control panel for defining new decision + score tabs */}
                    <div className={classes.rowBlack}>

                        {/* Digital clock */}
                        <div className={classes.digitalClockContainer}>
                            <IconButton
                                className={classes.fastForwardRewindButton}
                                onClick={fastRewind}
                                size="large">
                                <ChevronLeft className={classes.fastForwardRewindIcon}/>
                            </IconButton>

                            <div className={classes.digitalClock}>
                                {moment(videoProgress * 1000).format("mm:ss:SS")}
                            </div>

                            <IconButton
                                className={classes.fastForwardRewindButton}
                                onClick={fastForward}
                                size="large">
                                <ChevronRight className={classes.fastForwardRewindIcon}/>
                            </IconButton>
                        </div>

                        {/* Tab labels (tab headers) */}
                        <TabList
                            variant="scrollable"
                            scrollButtons="auto"
                            onChange={handleChangeScoreDefinitionTab}
                            aria-label="scores tab">
                            {scoreDefinitions.map((scoreDefinition, index) => (
                                <Tab key={`tab-${index}`}
                                     disabled={blurDrawingMode || drawingMode || previewInProgress || videoPlaying}
                                     label={getScoreDefinitionTabLabel(scoreDefinition)}
                                     value={index.toString()}/>
                            ))}
                        </TabList>

                        {/* Adding of a new decision respectively showing info why no additional decision is allowed */}
                        {getNewDecisionSection()}
                    </div>

                    {/* Tab content panels (selected score definition matched by the activeScoreDefinitionIndex value) */}
                    {!fullScreenMode && scoreDefinitions.map((scoreDefinition, index) => (
                        <TabPanel key={`tab-panel-${index}`} value={index.toString()}>

                            {/* Score definition table panel (single-row table only) */}
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="left"></TableCell>
                                            <TableCell align="center">
                                                {intl.formatMessage({
                                                    id: 'app.scoredScene.edit.scores.table.column.time',
                                                    defaultMessage: 'Time'
                                                })}
                                            </TableCell>
                                            <TableCell align="center">
                                                {intl.formatMessage({
                                                    id: 'app.scoredScene.edit.scores.table.column.athlete',
                                                    defaultMessage: 'Athlete'
                                                })}
                                            </TableCell>
                                            <TableCell align="center">
                                                {intl.formatMessage({
                                                    id: 'app.scoredScene.edit.scores.table.column.technique',
                                                    defaultMessage: 'Technique'
                                                })}
                                            </TableCell>
                                            <TableCell align="center">
                                                {intl.formatMessage({
                                                    id: 'app.scoredScene.edit.scores.table.column.score',
                                                    defaultMessage: 'Score'
                                                })}
                                            </TableCell>
                                            <TableCell align="right">
                                                {intl.formatMessage({
                                                    id: 'app.scoredScene.edit.scores.table.column.actions',
                                                    defaultMessage: 'Actions'
                                                })}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow key={index}
                                                  className={getRowClass(scoreDefinition, index)}>

                                            <TableCell>
                                                <IconButton onClick={() => jumpToScoreDefinition(index)}
                                                            size="large">
                                                    <ChevronRight className={classes.controlIcon}/>
                                                </IconButton>
                                            </TableCell>

                                            {/* Time */}
                                            <TableCell>

                                                {/* The time is decorated with a basic hint in case the frame has not yet been seeked */}
                                                <BasicHint
                                                    options={{
                                                        visible: !scoreDefinition.frameAlreadySeeked,
                                                        placement: 'right'
                                                    }}
                                                    element={<fieldset
                                                        disabled={!isOperationOnObjectAllowed(selectedScoredScene)}
                                                        style={{borderWidth: 0}}>
                                                        <div className={classes.row}>
                                                            <ArrowBackIos
                                                                onClick={() => pictureBack(index)}
                                                                className={classes.controlIcon}/>

                                                            <div className={classes.digitalClockSmall}>
                                                                {moment(scoreDefinition.time * 1000).format("mm:ss:SS")}
                                                            </div>

                                                            <ArrowForwardIos
                                                                onClick={() => pictureForward(index)}
                                                                className={classes.controlIcon}/>
                                                        </div>
                                                        <div className={classes.row}>
                                                            <b>{getScoreTimeWarningText(scoreDefinition)}</b>
                                                        </div>
                                                        <div className={classes.row}>
                                                            <Button variant="outlined" size="small"
                                                                    className={classes.setButton}
                                                                    onClick={() => setCurrentVideoTimeInScoreDefinition(index)}>
                                                    <span>
                                                        {intl.formatMessage({
                                                            id: 'app.scoredScene.edit.scores.table.column.time.setTime',
                                                            defaultMessage: 'Set to <code>{time}</code>'
                                                        }, {
                                                            time: moment(videoProgress * 1000).format("mm:ss:SS"),
                                                            code: (component) => <b>{component}</b>
                                                        })}
                                                    </span>
                                                            </Button>
                                                        </div>
                                                    </fieldset>}
                                                    hintText={<div className={classes.timeInfoText}>
                                                        {intl.formatMessage({
                                                            id: 'app.scoredScene.edit.scores.table.instructions.fineTuneTime',
                                                            defaultMessage: 'Use arrows to fine-tune the time!'
                                                        })}
                                                    </div>}/>
                                            </TableCell>

                                            {/* Athlete */}
                                            <TableCell align="center"
                                                       className={getClassInBlurMode(null, !scoreDefinition.frameAlreadySeeked)}>
                                                <fieldset
                                                    disabled={!isOperationOnObjectAllowed(selectedScoredScene)}
                                                    style={{borderWidth: 0}}>
                                                    <FormControlLabel
                                                        control={
                                                            // We use to versions of switches to reflect the left/right positions of the athletes as well
                                                            athleteLeftPosition === 'red'
                                                                ? <AthleteSwitchLeftRed
                                                                    checked={scoreDefinition.athlete === 'blue'}
                                                                    onChange={(event) => handleChangeAthlete(event, index)}
                                                                    name="reg-score-athlete"
                                                                    disabled={!scoreDefinition.frameAlreadySeeked}/>
                                                                : <AthleteSwitchLeftBlue
                                                                    checked={scoreDefinition.athlete === 'red'}
                                                                    onChange={(event) => handleChangeAthlete(event, index)}
                                                                    name="reg-score-athlete"
                                                                    disabled={!scoreDefinition.frameAlreadySeeked}/>
                                                        }
                                                        label={intl.formatMessage({
                                                            id: `app.ids.athlete.${scoreDefinition.athlete}`,
                                                            defaultMessage: scoreDefinition.athlete
                                                        })}
                                                    />
                                                </fieldset>
                                            </TableCell>

                                            {/* Technique (also determines the score) */}
                                            <TableCell align="center"
                                                       className={getClassInBlurMode(null, !scoreDefinition.frameAlreadySeeked)}>
                                                <fieldset
                                                    disabled={!isOperationOnObjectAllowed(selectedScoredScene)}
                                                    style={{borderWidth: 0}}>
                                                    <div className={classes.techniqueColumn}>
                                                        <Select style={{minWidth: 250}}
                                                                id="reg-score-technique-input"
                                                                value={scoreDefinition.technique}
                                                                onChange={(event) => handleChangeTechnique(event, index)}
                                                                size="small"
                                                                disabled={!scoreDefinition.frameAlreadySeeked}>
                                                            {getTechniqueMenuItems()}
                                                        </Select>

                                                        {/* Free text (single tag) in case "other penalty" is selected - can be applied for other techniques in the future if required */}
                                                        {disciplineStructure.scores.find((s) => s.technique === scoreDefinition.technique)?.freeText &&
                                                        <SuggestionTextField
                                                            variant="standard"
                                                            size="small"
                                                            style={{minWidth: 250, marginTop: 15}}
                                                            id='reg-score-techniqueFreeText-input'
                                                            placeholder={intl.formatMessage({
                                                                id: 'app.scoredScene.edit.scores.table.techniqueFreeText.placeholder',
                                                                defaultMessage: 'Add description'
                                                            })}
                                                            existingOptions={autocompleteOptions?.otherPenaltyTechniques || []}
                                                            value={scoreDefinition.techniqueFreeText}
                                                            onChange={(value) => handleChangeTechniqueFreeText(value, index)}
                                                        />}
                                                    </div>

                                                </fieldset>
                                            </TableCell>

                                            <TableCell align="center"
                                                       className={getClassInBlurMode(null, !scoreDefinition.frameAlreadySeeked)}>
                                                <div style={{fontSize: 18, fontWeight: "bold"}}>
                                                    {scoreDefinition.score}
                                                </div>
                                            </TableCell>

                                            <TableCell align="right"
                                                       className={getClassInBlurMode(null, !scoreDefinition.frameAlreadySeeked)}>

                                                {/* Slow motion preview button */}
                                                {!scoreDefinition.concurrent && <IconButton
                                                    disabled={previewInProgress || videoPlaying || drawingMode || !scoreDefinition.frameAlreadySeeked || blurDrawingMode || !isScoreWithinFence(scoreDefinition)}
                                                    onClick={() => {
                                                        showPreview(scoreDefinition)
                                                    }}
                                                    size="large">
                                                    <SlowMotionVideo/>
                                                </IconButton>}

                                                {/* Drawing button */}
                                                {!scoreDefinition.concurrent && <IconButton
                                                    disabled={previewInProgress || videoPlaying || !scoreDefinition.frameAlreadySeeked || !isOperationOnObjectAllowed(selectedScoredScene) || drawingMode || blurDrawingMode || !isScoreWithinFence(scoreDefinition)}
                                                    onClick={() => {
                                                        handleEnterDrawingMode(scoreDefinition)
                                                    }}
                                                    size="large">
                                                    {scoreDefinition.highlighting
                                                        ? <PictureInPicture
                                                            className={drawingMode && activeDrawingScoreDefinition === scoreDefinition ? classes.activeDrawingIcon : null}/>
                                                        : <Brush
                                                            className={drawingMode && activeDrawingScoreDefinition === scoreDefinition ? classes.activeDrawingIcon : null}/>}
                                                </IconButton>}

                                                {/* Remove button */}
                                                {((disciplineStructure.mode === 'running_time' && scoreDefinitions.length > 1) || (scoreDefinition.concurrent)) &&
                                                <IconButton
                                                    disabled={previewInProgress || videoPlaying || drawingMode || !isOperationOnObjectAllowed(selectedScoredScene)}
                                                    onClick={() => removeScoreDefinition(index)}
                                                    size="large">
                                                    <Delete/>
                                                </IconButton>}

                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TabPanel>
                    ))}
                </TabContext>}
            </div>
        </div>
    );
}

export default GeneralEditScoredSceneForm
